import { Divider, Spin, Typography } from 'antd';
import { AIIcon, CrownIcon, NeutralIcon } from 'assets';
import { OmiChanelChatSourceIcon } from 'components/omni-channel-chat';
import { useContactDetail } from 'hooks/contacts';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import React from 'react';
import { ContactDto } from 'types';
import { AIContactEvaluationOption, CONTACT_PREVIEWS, pickRandom } from 'utils';
import { ContactNote } from './ContactNote';
import dayjs from 'dayjs';
import { useFindDetailContactSentiment } from 'hooks';
import { aiAgentCenterMessages } from 'messages';

type ContactDetailsProps = {
  contactId: number;
};

type PersonalInfoItemProps = {
  label: string;
  value?: string;
};

const PersonalInfo = ({ contact }: { contact: ContactDto }) => {
  const personalInfoItems: PersonalInfoItemProps[] = [
    {
      label: customerLookupMessages.phoneNumber,
      value: contact.phoneNumber
    },
    {
      label: customerLookupMessages.idNumber,
      value: contact.idNumber
    },
    {
      label: customerLookupMessages.dateProvided,
      value: contact.idIssuedDate ? dayjs(contact.idIssuedDate).format('DD/MM/YYYY') : ''
    },
    {
      label: customerLookupMessages.placeProvided,
      value: contact.idIssuedPlace
    }
  ];

  return (
    <div className='flex flex-col gap-4'>
      <Typography.Text className='text-xs font-semibold uppercase' type='secondary'>
        {customerLookupMessages.personalInfo}
      </Typography.Text>
      <div className='flex flex-col gap-3'>
        {personalInfoItems.map((item, index) => (
          <div key={`personal-info-${index}`} className='flex gap-4'>
            <div className='w-[88px]'>
              <Typography.Text className='text-[#707070]'>{item.label}:</Typography.Text>
            </div>
            <div className='flex-1'>
              <Typography.Text>{item.value || '-'}</Typography.Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AccountSocials = ({ contactSocials }: { contactSocials: ContactDto['contactSocialMedias'] }) => {
  return (
    <div className='flex flex-col gap-4'>
      <Typography.Text className='text-xs font-semibold uppercase' type='secondary'>
        {customerLookupMessages.socialAddressAccount}
      </Typography.Text>
      <div className='flex flex-row flex-wrap items-center gap-6'>
        {contactSocials?.map((social, index) => (
          <div key={`social-${index}`} className='flex items-center gap-2'>
            <OmiChanelChatSourceIcon size={24} source={social.source} />
            <Typography.Text className='text-sm'>{social.customerName}</Typography.Text>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContactVipCustomer = ({ vipCustomer }: { vipCustomer: ContactDto['vipCustomer'] }) => {
  return (
    <div className='flex flex-col gap-[18px]'>
      <div className='flex gap-4'>
        <div>
          <div className='flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#FF950029]'>
            <CrownIcon className='h-6 w-6' />
          </div>
        </div>
        <div className='flex flex-col gap-1'>
          <Typography.Text className='font-semibold'>{customerLookupMessages.customerVip}</Typography.Text>
          <div className='flex items-center gap-2'>
            <div
              className='h-2 w-4'
              style={{
                background: vipCustomer?.customerGroup?.color ?? '#f5f5f7'
              }}
            />
            <Typography.Text className='text-sm'>{vipCustomer?.customerGroup?.name}</Typography.Text>
          </div>
        </div>
      </div>
      {vipCustomer?.note && (
        <div className='bg-white p-5'>
          <Typography.Paragraph className='mb-0'>{vipCustomer.note}</Typography.Paragraph>
        </div>
      )}
    </div>
  );
};

const AIContactPreview = ({ contact, preview }: { contact: ContactDto; preview: ContactDto['contactSentiment'] }) => {
  const optionEvaluation = preview?.sentiment ? AIContactEvaluationOption[preview.sentiment] : null;
  const { detail, isLoadingDetailSentiment } = useFindDetailContactSentiment({
    idNumber: contact.idNumber!,
    phoneNumber: contact.phoneNumber!
  });
  if (isLoadingDetailSentiment) {
    return (
      <div className='skeleton-active relative h-24 rounded-lg p-5'>
        <div className='absolute left-1/2 top-5 -translate-x-1/2'>
          <div className='flex flex-col items-center justify-center gap-2'>
            <Spin spinning />
            <Typography.Text className='text-colorPrimary'>
              {aiAgentCenterMessages.loadingContactSentiment}
            </Typography.Text>
          </div>
        </div>
      </div>
    );
  }
  if (detail && (detail.advice || detail.violations.length > 0))
    return (
      <div
        className='rounded-lg p-5'
        style={{
          background: optionEvaluation?.muted
        }}
      >
        <div className='flex items-center gap-4'>
          <div>
            <AIIcon height={44} width={44} />
          </div>
          <div className='flex flex-col gap-1'>
            <Typography.Text className='text-xs leading-4'>
              {customerLookupMessages.behaviorCharacteristics}
            </Typography.Text>
            <Typography.Text className='font-semibold'>{detail?.violations.join(', ')}</Typography.Text>
          </div>
        </div>
        <div className='mt-6 flex flex-col gap-2'>
          <Typography.Text className='text-xs font-semibold'>
            {customerLookupMessages.proposeCommunicationSolution}
          </Typography.Text>
          <Typography.Paragraph className='mb-0'>{detail?.advice}</Typography.Paragraph>
        </div>
      </div>
    );
  return null;
};

const ContactDetails: React.FC<ContactDetailsProps> = ({ contactId }) => {
  const { contact, isLoading, isLoadingContactSentiment } = useContactDetail(contactId);

  const optionEvaluation = contact?.contactSentiment?.sentiment
    ? AIContactEvaluationOption[contact.contactSentiment?.sentiment]
    : null;

  if (isLoading || !contact) {
    return (
      <div className='relative flex w-full'>
        <div className='absolute left-1/2 top-1/3 -translate-x-1/2'>
          <div className='flex flex-col items-center justify-center gap-2'>
            <Spin spinning />
            <Typography.Text className='text-colorPrimary'>{aiAgentCenterMessages.loadingContact}</Typography.Text>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='w-full pt-6'>
      <div className='flex gap-5 px-6 pb-5'>
        <div className='flex h-14 w-14 items-center justify-center'>
          <Spin spinning={isLoadingContactSentiment}>
            {optionEvaluation ? optionEvaluation.icon(56) : <NeutralIcon height={56} width={56} />}
          </Spin>
        </div>
        <div className='flex flex-col gap-1.5'>
          <Typography.Text className='text-2xl font-semibold'>{contact.name}</Typography.Text>
          <span
            className='inline-block w-fit rounded-base px-1.5 text-xs font-semibold uppercase leading-5 text-white'
            style={{
              background: optionEvaluation?.color
            }}
          >
            {optionEvaluation?.label}
          </span>
        </div>
      </div>
      <div className='max-h-[calc(100vh-258px)] overflow-auto px-6 pb-[128px] pt-5'>
        <div className='mb-8'>
          <PersonalInfo contact={contact} />
        </div>
        <AccountSocials contactSocials={contact.contactSocialMedias} />
        <Divider className='mb-6 mt-5' />
        {contact.vipCustomer && (
          <>
            <ContactVipCustomer vipCustomer={contact.vipCustomer} />
            <Divider className='mb-6 mt-5' />
          </>
        )}

        <div className='flex flex-col gap-4'>
          {contact.contactSentiment?.sentiment && (
            <AIContactPreview contact={contact} preview={contact.contactSentiment} />
          )}
          <ContactNote customerName={contact.name} note={contact.note} contactId={contactId} user={contact.updatedBy} />
        </div>
        <Divider className='mb-6 mt-5' />
      </div>
    </div>
  );
};

export default ContactDetails;
